import type { AxiosInstance } from "axios";

import { parseAxiosResponse } from "@thalamos/common";
import type {
  AllocateResponsibleUserRequest,
  EndInvolvementsRequest,
  PartyReach,
  PersonId,
  PersonInvolvement,
  ReplaceResponsibleUserRequest,
  StartInvolvementsRequest,
} from "@thalamos/haven-common";
import {
  PartyReachSchema,
  PersonInvolvementSchema,
} from "@thalamos/haven-common";

import type { ErrorBody, ResponseBody } from "../axios/api.js";
import { useApiAxios } from "../axios/api.js";

const parseInvolvements = parseAxiosResponse(PersonInvolvementSchema.array());
const parseReach = parseAxiosResponse(PartyReachSchema.array());

/**
 * Who can reach a young person, and what one party can reach — the same rows
 * read from either end.
 *
 * Every response that is read back is parsed, `actions` included. That list is
 * worked out by the server from the involvement and the holder's role; nothing
 * here recomputes it, because a page composing its own account of what an
 * involvement means would be a second implementation of the policy.
 */
export const config = {
  involvement: {
    forPerson: (personId: string) => ({
      url: `/api/persons/${personId}/involvements`,
      method: "get",
      transformResponse: parseInvolvements,
    }),
    forUser: (userId: string) => ({
      url: `/api/users/${userId}/reach`,
      method: "get",
      transformResponse: parseReach,
    }),
    /** Starts involvements for parties in one role, all of them or none. */
    start: (personId: PersonId, body: StartInvolvementsRequest) => ({
      url: `/api/persons/${personId}/involvements/start`,
      method: "post",
      data: body,
    }),
    /** Names who runs a case nobody runs yet. */
    allocateResponsibleUser: (
      personId: PersonId,
      body: AllocateResponsibleUserRequest,
    ) => ({
      url: `/api/persons/${personId}/allocate-responsible-user`,
      method: "post",
      data: body,
    }),
    /** Hands a case somebody runs to a successor, saying why. */
    replaceResponsibleUser: (
      personId: PersonId,
      body: ReplaceResponsibleUserRequest,
    ) => ({
      url: `/api/persons/${personId}/replace-responsible-user`,
      method: "post",
      data: body,
      transformResponse: parseInvolvements,
    }),
    /** Ends a set of involvements, with one cause and one author. */
    end: (personId: PersonId, body: EndInvolvementsRequest) => ({
      url: `/api/persons/${personId}/involvements/end`,
      method: "post",
      data: body,
      transformResponse: parseInvolvements,
    }),
  },
};

/**
 * A write that creates involvements answers 201, which `parseAxiosResponse`
 * leaves alone — so the body is parsed here when it arrives. Without this the
 * dates reach a caller as the strings they were sent as, and a grid formatting
 * one throws. A refusal keeps its error body.
 */
const parseCreated = (response: {
  status: number;
  data: unknown;
}): ResponseBody<PersonInvolvement[], ErrorBody> =>
  response.status === 201
    ? PersonInvolvementSchema.array().parse(response.data)
    : (response.data as ErrorBody);

export const api = (apiAxios: AxiosInstance) => {
  return {
    involvement: {
      start: async (personId: PersonId, body: StartInvolvementsRequest) => {
        const response = await apiAxios.request<unknown>(
          config.involvement.start(personId, body),
        );

        return { status: response.status, data: parseCreated(response) };
      },
      allocateResponsibleUser: async (
        personId: PersonId,
        body: AllocateResponsibleUserRequest,
      ) => {
        const response = await apiAxios.request<unknown>(
          config.involvement.allocateResponsibleUser(personId, body),
        );

        return { status: response.status, data: parseCreated(response) };
      },
      replaceResponsibleUser: (
        personId: PersonId,
        body: ReplaceResponsibleUserRequest,
      ) =>
        apiAxios.request<ResponseBody<PersonInvolvement[], ErrorBody>>(
          config.involvement.replaceResponsibleUser(personId, body),
        ),
      end: (personId: PersonId, body: EndInvolvementsRequest) =>
        apiAxios.request<ResponseBody<PersonInvolvement[], ErrorBody>>(
          config.involvement.end(personId, body),
        ),
    },
  };
};

export const apiHooks = () => {
  return {
    involvement: {
      forPerson: function useInvolvementsForPerson(personId: string) {
        return useApiAxios<PersonInvolvement[]>(
          config.involvement.forPerson(personId),
        );
      },
      forUser: function useReachForUser(userId: string) {
        return useApiAxios<PartyReach[]>(config.involvement.forUser(userId));
      },
    },
  };
};
